import Dialog from '@mui/material/Dialog';
import { DialogTitle, InputAdornment, TextField, FormControlLabel, Checkbox } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useState, useEffect, useRef } from 'react';
import { startCase, toLower } from 'lodash';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { LinkAllocation } from '../../../store/Link';
import { Icon } from '../../Atoms/Icon';
import { ConfirmationModal } from '../../Atoms';

export interface DialogParams {
  allocation?: LinkAllocation;
  open: boolean;
  setOpen: (state: boolean) => void;
  handleSubmit: (data: dataForm) => Promise<boolean | undefined>;
  handleDelete: () => Promise<boolean | undefined>;
}

export interface dataForm {
  allocationPercent: string | null;
  repeatOverride: boolean;
}

const initialFormData: dataForm = {
  allocationPercent: null,
  repeatOverride: false
};

export const AllocationOverrideModal = ({ allocation, open, setOpen, handleSubmit, handleDelete }: DialogParams) => {
  const [formData, setFormData] = useState<dataForm>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (allocation?.allocationPercent) {
      setFormData({
        ...initialFormData,
        repeatOverride: allocation.linkRepeatingOverrideEnabled || false,
        allocationPercent: cleanInput(String(allocation.allocationPercent))
      });
    }
  }, [allocation?.id]);

  useEffect(() => {
    setIsSaving(false);
    setIsDeleting(false);
    if (open) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleSubmitForm = () => {
    if (formData.allocationPercent !== null) {
      const numericValue = Number(formData.allocationPercent);
      if (!isNaN(numericValue)) {
        setIsSaving(true);
        handleSubmit(formData).finally(() => setIsSaving(false));
      }
    }
  };

  function cleanInput(input: string): string {
    let cleaned = input.replace(/[^0-9.]/g, '');

    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    if (cleaned.includes('.')) {
      const [whole, decimal] = cleaned.split('.');
      cleaned = `${whole || '0'}.${decimal.slice(0, 2)}`;
    }

    return cleaned;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    const cleaned = cleanInput(input);
    if (cleaned === '' && input !== '') return;
    setFormData((prev) => ({
      ...prev,
      allocationPercent: cleaned
    }));
  };

  if (!allocation) {
    return null;
  }

  const handleDeleteOverride = () => {
    setIsDeleting(true);
    handleDelete();
  };

  const iconAsset = allocation.assetType === 'asset' ? 'assetSmall' : 'provisionalAssetSm';
  const disabledForm = isSaving || isDeleting;
  return (
    <Dialog
      data-testid="repeating-override-modal"
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      fullWidth>
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          Override Allocation
        </TextBlock>
      </DialogTitle>
      <DialogContent
        sx={{
          minHeight: 70,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'left',
          overflowY: 'visible',
          margin: '1rem 4rem 2rem',
          padding: 0
        }}>
        <div className="flex flex-row gap-x-3 w-full">
          <div className="space-y-1 justify-center w-1/2">
            <TextBlock
              color="grey6"
              size="sm">
              {startCase(toLower(allocation.assetType))}
            </TextBlock>
            <div className="flex gap-1">
              <Icon
                className="p-2"
                type={iconAsset}
              />
              {allocation.assetNumber}
            </div>
          </div>
          <div className="space-y-1 justify-center w-1/2 mb-4">
            <TextBlock
              color="grey6"
              size="sm">
              Allocation
            </TextBlock>
            <TextField
              value={formData.allocationPercent}
              disabled={disabledForm}
              className="w-3/5"
              onChange={handleInputChange}
              inputRef={inputRef}
              data-testid="repeating-override-input"
              size="small"
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }
              }}
            />
          </div>
        </div>
        <div className="w-full">
          <hr className="my-4 -mx-5" />
        </div>
        <div className="flex flex-col w-full">
          <FormControlLabel
            disabled={disabledForm}
            control={
              <Checkbox
                checked={formData.repeatOverride}
                onChange={() => setFormData({ ...formData, repeatOverride: !formData.repeatOverride })}
              />
            }
            label={<TextSpan size="sm14">Repeat Override on all current and future Processing runs.</TextSpan>}
          />
        </div>
        {allocation.isManualOverride && <hr className="my-4 w-full" />}
        {allocation.isManualOverride && (
          <div className="flex justify-start w-full">
            <button
              disabled={disabledForm}
              className="text-sm text-red rounded my-2 py-2 px-4 bg-[#FFEFEF] hover:bg-[#FDD3D3]"
              onClick={() => setOpenConfirmation(true)}>
              <DeleteIcon
                color="red"
                className="mr-2 -mt-1"
                fontSize="small"
              />
              <span>{isDeleting ? 'Deleting...' : 'Clear Override'}</span>
            </button>
            <ConfirmationModal
              open={openConfirmation}
              setOpen={setOpenConfirmation}
              handleSubmit={handleDeleteOverride}
              text="Are you sure you want to delete this Allocation Override?"
              confirmButtonText="Delete"
            />
          </div>
        )}
      </DialogContent>
      <hr className="mb-1 w-full" />
      <DialogActions sx={{ marginRight: 2, pb: 2 }}>
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          disabled={disabledForm}
          onClick={handleClose}>
          Cancel
        </button>
        <button
          onClick={handleSubmitForm}
          disabled={!formData.allocationPercent || isNaN(Number(formData.allocationPercent)) || disabledForm}
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          type="submit">
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
